import { forwardRef, useEffect, useState } from 'react';
import RDP from 'react-datepicker';
import dayjs, { type Dayjs } from 'dayjs';
import { Calendar, X } from 'lucide-react';
import { cn } from './cn';
import { POPOVER_PORTAL_ID, keepPopperOnScreen } from './popoverPortal';

// Translate the common Ant Design (dayjs) format tokens callers pass to date-fns tokens.
function toDateFns(fmt?: string): string {
  if (!fmt) return 'dd MMM yyyy';
  return fmt.replace(/YYYY/g, 'yyyy').replace(/DD/g, 'dd').replace(/A/g, 'a');
}

const SIZE: Record<string, string> = { small: 'h-8 text-sm', middle: 'h-9 text-sm', large: 'h-11 text-[15px]' };

/**
 * Width (in `ch`) the value text needs so a field never clips its OWN value — "07 Aug 20…".
 * Derived from the date-fns format because most tokens render their own length (dd→2, MMM→3,
 * yyyy→4); `MMMM` and the am/pm token are the exceptions. +1 for wider-than-`0` capitals.
 */
function fmtCh(df: string, placeholder?: string): number {
  const rendered = df.replace(/MMMM/g, 'MMMMMMMMM').replace(/a+/g, 'PM');
  return Math.max(rendered.length, placeholder?.length ?? 0) + 1;
}

const DateInput = forwardRef<HTMLButtonElement, any>(function DateInput({ value, displayValue, onClick, onClear, placeholder, size = 'middle', disabled, allowClear, minCh, id, 'aria-label': ariaLabel, 'aria-invalid': ariaInvalid, 'aria-describedby': describedBy }, ref) {
  // What the field PRINTS. `displayValue` is for a picker whose value stands for a period rather
  // than a day — a week stepper reads "07 – 12 Sep", not "07 Sep" — while the picked day itself is
  // unchanged underneath, so the calendar still opens on it and `onChange` still returns one date.
  const shown = displayValue || value;
  return (
    <button
      type="button" ref={ref} onClick={onClick} disabled={disabled}
      /* `id` is what the enclosing Form.Item's <label htmlFor> points at. Without it a date field has
         no accessible name at all — a screen reader announces only the placeholder, "Select date". */
      id={id} aria-label={ariaLabel} aria-invalid={ariaInvalid} aria-describedby={describedBy}
      className={cn('inline-flex w-full min-w-[9rem] items-center gap-2 rounded-control border border-line bg-surface px-3 text-left text-content transition-colors hover:border-brand-300 focus-visible:border-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/20 disabled:cursor-not-allowed disabled:opacity-60', SIZE[size])}
    >
      <Calendar className="size-4 shrink-0 text-subtle" />
      <span className={cn('flex-1 truncate', !shown && 'text-subtle')} style={minCh ? { minWidth: `${minCh}ch` } : undefined}>{shown || placeholder}</span>
      {allowClear && shown && (
        <span role="button" tabIndex={-1} aria-label="Clear" onClick={(e) => { e.stopPropagation(); onClear?.(); }} className="grid size-5 place-items-center rounded-full text-subtle hover:bg-surface-2 hover:text-content">
          <X className="size-3.5" />
        </span>
      )}
    </button>
  );
});

/** Ant Design single `DatePicker` replacement (Dayjs value in/out), on react-datepicker. */
function DatePickerBase({
  value, onChange, format, displayValue, disabledDate, allowClear = true, placeholder = 'Select date', size = 'middle',
  disabled, picker, showTime, className, style, minDate, maxDate, dayClassName,
  id, 'aria-label': ariaLabel, 'aria-invalid': ariaInvalid, 'aria-describedby': describedBy,
}: {
  value?: Dayjs | null;
  onChange?: (d: Dayjs | null, dateString: string) => void;
  format?: string;
  /** Overrides the printed text when the value stands for a PERIOD the format cannot spell — a week
   *  or a fortnight. The calendar, the selected day and `onChange` are untouched. */
  displayValue?: string;
  disabledDate?: (d: Dayjs) => boolean;
  /** Extra class per day cell — for marking days that already mean something (e.g. a claim exists).
   *  Optional and defaulted off, so every existing caller is unaffected. */
  dayClassName?: (d: Dayjs) => string | undefined;
  allowClear?: boolean;
  placeholder?: string;
  size?: 'small' | 'middle' | 'large';
  disabled?: boolean;
  picker?: 'date' | 'month' | 'year';
  showTime?: boolean | Record<string, unknown>;
  className?: string;
  style?: import('react').CSSProperties;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  /** Passed in by `Form.Item` so the field's label actually points at this control. */
  id?: string;
  /** Names the field on a filter bar / toolbar, where there is no Form.Item label to point at it.
   *  react-datepicker never clones `aria-label` onto `customInput`, so it survives on the button. */
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}) {
  const base = toDateFns(format);
  // Only append a time part when the caller's own format doesn't already carry one — otherwise a
  // format like "DD MMM YYYY, hh:mm A" rendered "07 Aug 2026, 05:30 PM 17:30" (time printed twice).
  const df = picker === 'month' ? 'MMM yyyy' : picker === 'year' ? 'yyyy'
    : showTime && !/[Hhms]/.test(base) ? `${base} HH:mm` : base;
  return (
    // react-datepicker wraps its input in a `.react-datepicker-wrapper` that is inline-block and
    // content-sized, so a caller asking for `w-full` / width:100% got a full-width ROOT with a
    // narrow button rattling around inside it — which read as the field being detached from its
    // column. Stretch the wrapper so the width a caller sets actually reaches the control, and keep
    // the root inline-block so a picker with NO width still sizes to its content in a filter bar.
    <div
      className={cn(
        'gb-datepicker inline-block max-w-full',
        '[&_.react-datepicker-wrapper]:block [&_.react-datepicker-wrapper]:w-full',
        className,
      )}
      style={style}
    >
      <RDP
        selected={value ? value.toDate() : null}
        onChange={(d: Date | null) => onChange?.(d ? dayjs(d) : null, d ? dayjs(d).format('YYYY-MM-DD') : '')}
        dateFormat={df}
        showMonthYearPicker={picker === 'month'}
        showYearPicker={picker === 'year'}
        showTimeSelect={!!showTime}
        // `showTime={{ minuteStep: 5 }}` (Ant's spelling) sets the time list's step; otherwise the library's 30 minutes.
        timeIntervals={showTime && typeof showTime === 'object' && Number(showTime.minuteStep) > 0 ? Number(showTime.minuteStep) : undefined}
        filterDate={disabledDate ? (d: Date) => !disabledDate(dayjs(d)) : undefined}
        dayClassName={dayClassName ? (d: Date) => dayClassName(dayjs(d)) ?? '' : undefined}
        minDate={minDate ? minDate.toDate() : undefined}
        maxDate={maxDate ? maxDate.toDate() : undefined}
        disabled={disabled}
        placeholderText={placeholder}
        // react-datepicker clones `customInput` with its OWN input props, `id` among them — so an id
        // set only on the custom element below is overwritten with undefined and the field loses the
        // label association again. It has to be handed to the picker itself.
        id={id}
        ariaInvalid={ariaInvalid ? 'true' : undefined}
        ariaDescribedBy={describedBy}
        popperClassName="z-[var(--z-popover)]"
        // Shift, not just flip: a calendar on a phone must not run off the screen's edge.
        popperModifiers={[keepPopperOnScreen]}
        // Render the calendar at <body> instead of inline. Inline, a picker sitting near the
        // right/bottom edge of a Modal or Drawer was clipped by that body's `overflow-y-auto`
        // (react-datepicker only runs `flip`, never `shift`, so it has nothing to pull an
        // overhanging popper back into view). See popoverPortal.ts for the dismissal trade-off.
        portalId={POPOVER_PORTAL_ID}
        customInput={<DateInput id={id} aria-label={ariaLabel} aria-invalid={ariaInvalid} aria-describedby={describedBy} size={size} disabled={disabled} allowClear={allowClear} displayValue={displayValue} minCh={Math.max(fmtCh(df, placeholder), displayValue ? displayValue.length + 1 : 0)} onClear={() => onChange?.(null, '')} />}
      />
    </div>
  );
}

/** Ant Design `DatePicker.RangePicker` replacement — two composed date fields. */
function RangePicker({
  value, onChange, disabledDate, size = 'middle', disabled, format, className, style, allowEmpty, allowClear = false, showTime, placeholder,
  allowPartial = false,
  'aria-label': ariaLabel,
}: {
  value?: [Dayjs | null, Dayjs | null] | null;
  onChange?: (dates: [Dayjs | null, Dayjs | null] | null, dateStrings: [string, string]) => void;
  disabledDate?: (d: Dayjs) => boolean;
  size?: 'small' | 'middle' | 'large';
  disabled?: boolean;
  format?: string;
  className?: string;
  style?: import('react').CSSProperties;
  allowEmpty?: [boolean, boolean];
  allowClear?: boolean;
  showTime?: boolean | Record<string, unknown>;
  placeholder?: [string, string];
  /**
   * Surface a HALF-PICKED range to the parent as `[from, null]` / `[null, to]` instead of swallowing it.
   *
   * Off by default: most callers do `range[0].format()` unguarded and a partial would blank the page.
   * FILTER BARS want it on. "From 01 Sep onwards" and "up to 10 Sep" are both real filters, and while a
   * half-pick stayed invisible to the parent it was also UNCLEARABLE by it — the parent's value never
   * changed, so a "Clear filters" button left the end date sitting in the field, filtering nothing and
   * looking like it filtered something. Pair it with `useUrlPartialRangeOrNull`, which holds exactly
   * this shape.
   */
  allowPartial?: boolean;
  /** Names the pair; each half is announced as "<name> from" / "<name> to". */
  'aria-label'?: string;
}) {
  // Hold the in-progress selection as an internal DRAFT. We only surface a COMPLETE range (both dates)
  // or a full clear to the parent — NEVER a half-picked [start, null]. That way every consumer can do
  // range[0].format()/range[1].format() without null-guarding; a half-pick used to blank the whole page.
  const cFrom = value?.[0] ?? null;
  const cTo = value?.[1] ?? null;
  const [draft, setDraft] = useState<[Dayjs | null, Dayjs | null]>([cFrom, cTo]);
  // Re-sync the draft when the parent changes the value (clear, preset, external set). Keyed on the
  // instants (valueOf) so a new-but-equal array reference can't wipe a half-made selection mid-pick.
  useEffect(() => { setDraft([cFrom, cTo]); }, [cFrom?.valueOf(), cTo?.valueOf()]); // eslint-disable-line react-hooks/exhaustive-deps

  const [from, to] = draft;
  const commit = (f: Dayjs | null, t: Dayjs | null) => {
    setDraft([f, t]); // always reflect the pick in the field…
    const strs: [string, string] = [f?.format('YYYY-MM-DD') ?? '', t?.format('YYYY-MM-DD') ?? ''];
    if (allowPartial) onChange?.(f || t ? [f, t] : null, strs); // every state, including one-ended
    else if (f && t) onChange?.([f, t], strs); // …but only notify on a complete range
    else if (!f && !t) onChange?.(null, ['', '']); // …or a full clear
  };
  // Each field FILLS and SHARES the picker's width (flex-1 + react-datepicker wrapper stretched to full,
  // button min-width dropped) so a caller-set width divides cleanly between the two fields instead of the
  // two 9rem-min buttons overflowing onto neighbouring controls.
  // `min-w-fit` is what keeps a field from being squeezed below the width its own formatted date needs —
  // a caller-set width (w-68, w-full) still SHARES out any EXTRA space, but can no longer clip the value
  // to "07 Aug 20…". The floor comes from the format itself (see fmtCh), so date+time ranges reserve more.
  const fieldCls = 'min-w-fit flex-1 [&_.react-datepicker-wrapper]:block [&_.react-datepicker-wrapper]:w-full [&_button]:min-w-0';
  return (
    // `flex-wrap` is what keeps `min-w-fit` honest. The two fields refuse to shrink below the width
    // their own formatted date needs (deliberately — see fieldCls), so on a ~360px phone the pair is
    // ~340px against ~328px of usable row and simply overflowed the `max-w-full` cap, pushing every
    // list page that carries a date filter sideways. Wrapping drops the To field to a second line
    // instead; on any screen wide enough for both, nothing moves.
    <div className={cn('inline-flex max-w-full flex-wrap items-center gap-1.5', className)} style={style}>
      <DatePickerBase className={fieldCls} value={from} onChange={(d) => commit(d, to)} disabledDate={disabledDate} size={size} disabled={disabled} format={format} showTime={showTime} allowClear={allowClear} placeholder={placeholder?.[0] ?? 'Start date'} maxDate={to ?? undefined} aria-label={ariaLabel ? `${ariaLabel} from` : undefined} />
      <span className="shrink-0 text-subtle">→</span>
      <DatePickerBase className={fieldCls} value={to} onChange={(d) => commit(from, d)} disabledDate={disabledDate} size={size} disabled={disabled} format={format} showTime={showTime} allowClear={allowClear} placeholder={placeholder?.[1] ?? 'End date'} minDate={from ?? undefined} aria-label={ariaLabel ? `${ariaLabel} to` : undefined} />
    </div>
  );
}

/** Exported as `DatePicker`, with `DatePicker.RangePicker` (antd-compatible static). */
export const DatePicker = Object.assign(DatePickerBase, { RangePicker });
